import { useState } from "react"
import type { DateTime } from "luxon"
import { Stack, Typography } from "@mui/material"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../modules/localization/translations"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardAlertInfo } from "../../../../alert/StandardAlertInfo"
import { StandardButton } from "../../../../buttons/StandardButton"

import { formatRepeatDaysRange, getRepeatRangeErrorId, toRepeatDateKey } from "./helpers"
import type { RepeatDeliveryDay } from "./helpers"
import type { RepeatOrderFormValues } from "./RepeatOrderDialog"
import { RepeatDeliveryDaysList } from "./RepeatDeliveryDaysList"

// not filled in yet: the block stays hidden
const UNFILLED_RANGE_ERROR_IDS: ReadonlyArray<TranslationKey> = [
    "create-order-drawer-repeat-order-end-date-missing-error",
    "create-order-drawer-repeat-order-count-missing-error",
]

const useStyles = makeStyles()((theme) => ({
    card: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".25rem",
    },
    summary: {
        padding: ".75rem 1rem",
    },
    details: {
        padding: ".75rem 1rem 1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    muted: {
        color: theme.palette.tailwind.slate[500],
    },
    range: {
        fontWeight: 500,
    },
    showButton: {
        flexShrink: 0,
    },
}))

type RepeatDeliveryDaysProps = Readonly<{
    days: ReadonlyArray<RepeatDeliveryDay>
}>

export function RepeatDeliveryDays({ days }: RepeatDeliveryDaysProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { values, setFieldValue } = useFormikContext<RepeatOrderFormValues>()
    const [isOpen, setIsOpen] = useState(values.deselectedDates.length > 0)

    const rangeErrorId = getRepeatRangeErrorId(values)
    const deselectedCount = days.filter((day) => day.isDeselected).length

    const handleToggle = (date: DateTime) => {
        const dateKey = toRepeatDateKey(date)

        void setFieldValue(
            "deselectedDates",
            values.deselectedDates.includes(dateKey)
                ? values.deselectedDates.filter((deselectedDate) => deselectedDate !== dateKey)
                : [...values.deselectedDates, dateKey],
        )
    }

    if (rangeErrorId && UNFILLED_RANGE_ERROR_IDS.includes(rangeErrorId)) return null

    return (
        <Stack gap=".5rem">
            <LocalizedStrict id="create-order-drawer-repeat-order-delivery-days">
                <Typography variant="subtitle1">Delivery days:</Typography>
            </LocalizedStrict>
            {rangeErrorId ? (
                <StandardAlertInfo severity="error" useDefaultStyles fullWidth title={l10n.getString(rangeErrorId)} />
            ) : (
                <Stack className={classes.card}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        gap=".75rem"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                        className={classes.summary}
                    >
                        <Stack gap=".25rem">
                            {days.length > deselectedCount ? (
                                <Typography variant="body1" className={classes.range}>
                                    {formatRepeatDaysRange(days, dateTimeFactory)}
                                </Typography>
                            ) : (
                                <LocalizedStrict id="create-order-drawer-repeat-order-delivery-days-none-selected">
                                    <Typography variant="body1" className={classes.range}>
                                        No days selected
                                    </Typography>
                                </LocalizedStrict>
                            )}
                            {days.length > 0 && (
                                <LocalizedStrict
                                    id={
                                        deselectedCount > 0
                                            ? "create-order-drawer-repeat-order-delivery-days-deselected"
                                            : "create-order-drawer-repeat-order-delivery-days-all-selected"
                                    }
                                    vars={{ count: deselectedCount }}
                                >
                                    <Typography variant="caption" className={classes.muted}>
                                        All days in the period are selected
                                    </Typography>
                                </LocalizedStrict>
                            )}
                        </Stack>
                        {days.length > 0 && (
                            <LocalizedStrict
                                id={
                                    isOpen
                                        ? "create-order-drawer-repeat-order-delivery-days-hide-button"
                                        : "create-order-drawer-repeat-order-delivery-days-show-button"
                                }
                                vars={{ count: days.length }}
                            >
                                <StandardButton
                                    type="button"
                                    color="blue"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={false}
                                    aria-expanded={isOpen}
                                    onClick={() => setIsOpen(!isOpen)}
                                    endIcon={<FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} size="xs" />}
                                    updateDefaultClasses={classes.showButton}
                                >
                                    View and deselect days
                                </StandardButton>
                            </LocalizedStrict>
                        )}
                    </Stack>
                    {isOpen && days.length > 0 && (
                        <Stack gap="1rem" className={classes.details}>
                            <Stack direction="row" gap=".75rem" alignItems="center" justifyContent="space-between">
                                <LocalizedStrict id="create-order-drawer-repeat-order-delivery-days-hint">
                                    <Typography variant="body2" className={classes.muted}>
                                        Untick the days that should not be delivered.
                                    </Typography>
                                </LocalizedStrict>
                                {deselectedCount > 0 && (
                                    <LocalizedStrict id="create-order-drawer-repeat-order-delivery-days-reset-button">
                                        <StandardButton
                                            type="button"
                                            color="blue"
                                            variant="text"
                                            size="small"
                                            fullWidth={false}
                                            updateDefaultClasses={classes.showButton}
                                            onClick={() => void setFieldValue("deselectedDates", [])}
                                        >
                                            Reset deselections
                                        </StandardButton>
                                    </LocalizedStrict>
                                )}
                            </Stack>
                            <RepeatDeliveryDaysList days={days} onToggle={handleToggle} />
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    )
}
